import { Cloudinary } from '@cloudinary/url-gen';

export type UploadingFileStatus = {
  progress: number,
  status: "waiting" | "errored" | "loading" | "done"
}

const cld = new Cloudinary({
  cloud: {
    cloudName: "dcmekntwa",
  },
});

export const buildUploadHandler = (dependencies: {
  addFile: (file: any) => void;
  updateFileStatus: (signature: string, status: UploadingFileStatus) => void;
}) => {

  const { addFile, updateFileStatus } = dependencies;

  const generateUniqueUploadId = () => {
    return `uqid-${Date.now()}`;
  };

  const createRequest = (sig: string) => {
    // on utilise XHR pour avoir accès au progrès de l'upload
    const xhr = new XMLHttpRequest();
    Object.entries({
      progress: (e: any) => updateFileStatus(sig, { status: "loading", progress: (e.loaded * 100.0) / e.total }),
      loadstart: (_e: any) => updateFileStatus(sig, { status: "loading", progress: 0 }),
      error: (_e: any) => {
        updateFileStatus(sig, { status: "errored", progress: 0 })
      },
    }).forEach(([k, f]: [string, (e: any) => void]) => xhr.upload.addEventListener(k, f));
    return xhr
  }

  const generateFormData = (file: Blob, signature: string, timestamp: string, preset?: string) => {
    const fd = new FormData();
    if (preset) { fd.append("upload_preset", preset) }
    fd.append("file", file);
    fd.append("api_key", "883898914556696");
    fd.append("timestamp", timestamp);
    fd.append("return_delete_token", "true");
    fd.append("signature", signature);
    return fd
  }

  const handleFinalResponse = (
    response: { resource_type: string, type: string, public_id: string, format: string, version: string },
    signature: string
  ) => {
    updateFileStatus(signature, { status: "done", progress: 100 })
    const identifier = `${response.resource_type}/${response.type}/v${response.version}/${response.public_id}.${response.format}`;
    addFile({ ...response, identifier });
  }

  const smallUpload = (url: string, file: Blob, signature: string, timestamp: string, preset?: string) => {
    const xhr = createRequest(signature);
    const fd = generateFormData(file, signature, timestamp, preset);
    xhr.onreadystatechange = function() {
      if (xhr.readyState == 4 && xhr.status == 200) {
        var response = JSON.parse(xhr.responseText);
        handleFinalResponse(response, signature)
      }
    };
    xhr.open("POST", url, true);
    xhr.setRequestHeader("X-Requested-With", "XMLHttpRequest");
    xhr.send(fd);
  }

  const bigUpload = (url: string, file: Blob, signature: string, timestamp: string, preset?: string) => {
    const uniqueUploadId = generateUniqueUploadId();
    const chunkSize = 5 * 1024 * 1024;
    const totalChunks = Math.ceil(file.size / chunkSize);
    let currentChunk = 0;

    const uploadChunk = async (start: number, end: number) => {
      const body = generateFormData(file.slice(start, end), signature, timestamp, preset);
      const contentRange = `bytes ${start}-${end - 1}/${file.size}`;
      try {
        const response = await fetch(
          url,
          {
            method: 'POST',
            body,
            headers: {
              'X-Unique-Upload-Id': uniqueUploadId,
              'Content-Range': contentRange,
            },
          }
        );

        if (!response.ok) { throw new Error('Chunk upload failed.'); }

        currentChunk++;

        if (currentChunk < totalChunks) {
          const nextStart = currentChunk * chunkSize;
          const nextEnd = Math.min(nextStart + chunkSize, file.size);
          uploadChunk(nextStart, nextEnd);
          updateFileStatus(signature, {
            status: "loading", progress: Math.round((nextStart / file.size) * 100)
          })
        } else {

          updateFileStatus(signature, { status: "done", progress: 100 })

          const fetchResponse = await response.json();
          handleFinalResponse(fetchResponse, signature)
        }
      } catch (error) {
        console.error('Error uploading chunk:',
          error);
      }
    }
    const start = 0;
    const end = Math.min(chunkSize, file.size);
    uploadChunk(start, end);
  }


  return async (files: File[]) => {
    files.forEach((file) => {
      const timestamp = Date.now().toString();
      const preset = "optimize"
      // On passe par le backend pour ne pas exposer le secret (sert à signer)
      fetch(
        `/api/sign_cloudinary_upload?timestamp=${timestamp}&return_delete_token=true${preset ? ("&upload_preset=" + preset) : ""}`
      ).then(async (signatureData) => {
        const { signature } = await signatureData.json();
        const url = `https://api.cloudinary.com/v1_1/${cld.getConfig().cloud?.cloudName
          }/upload`;
        if (file.size > 50_000_000) {
          bigUpload(url, file, signature, timestamp, preset)
        } else {
          smallUpload(url, file, signature, timestamp, preset)
        }
      });
    });
  };
};



